/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: VIPListsPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Edit, Trash2, Copy } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useDataGrid } from '../../hooks/useDataGrid';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import {
  deleteVipListById,
  navigateToVipListDuplicate,
} from './vipListsActions';
import { useContextStore } from '../../store/context';

interface VIPList {
  id: number;
  uuid: string;
  name: string;
  enabled: boolean;
  updatedAt: string | null;
}

export default function VIPListsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();
  const { selectedDomain } = useContextStore();

  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; list: VIPList | null }>({
    isOpen: false,
    list: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchLists = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const queryParams = new URLSearchParams({
          page: params.page.toString(),
          limit: params.limit.toString(),
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          ...(params.search ? { search: params.search } : {}),
          ...(selectedDomain?.id ? { domainId: selectedDomain.id.toString() } : {})
        });

        const response = await fetch(buildApiUrl(`/api/telephony/vip-lists?${queryParams}`), {
          headers: getApiHeaders()
        });

        if (!response.ok) {
          throw new Error(t('vipLists.messages.loadError'));
        }

        const result = await response.json();
        if (!result?.success) {
          throw new Error(result?.message || t('vipLists.messages.loadError'));
        }

        const data: VIPList[] = Array.isArray(result.data) ? result.data : [];
        const pagination = result.pagination || {};

        return {
          data,
          pagination: {
            page: pagination.page ?? params.page,
            limit: pagination.limit ?? params.limit,
            total: pagination.total ?? data.length,
            totalPages: pagination.totalPages ?? 0
          }
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : t('vipLists.messages.loadError');
        showError(message);
        throw error;
      }
    },
    [showError, t, selectedDomain?.id]
  );

  const availableColumns = useMemo(() => [
    {
      key: 'name',
      label: t('vipLists.columns.name'),
      visible: true,
      sortable: true,
      render: (_value: unknown, item: VIPList) => (
        <div className="flex items-center gap-2">
          <Crown className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-foreground">{item?.name ?? ''}</span>
        </div>
      )
    },
    {
      key: 'enabled',
      label: t('vipLists.columns.enabled'),
      visible: true,
      sortable: true,
      render: (_value: unknown, item: VIPList) => (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.enabled
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            }`}
        >
          {item?.enabled ? t('common.enabled') : t('common.disabled')}
        </span>
      )
    },
    {
      key: 'updatedAt',
      label: t('vipLists.columns.updatedAt'),
      visible: true,
      sortable: true,
      render: (_value: unknown, item: VIPList) => (
        <span className="text-sm text-muted-foreground">
          {item?.updatedAt ? new Date(item.updatedAt).toLocaleString() : '�'}
        </span>
      )
    },
    {
      key: 'actions',
      label: t('common.actions'),
      visible: true,
      sortable: false
    }
  ], [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData: fetchLists,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialColumns: availableColumns,
    reloadKey: selectedDomain?.id ?? null
  });

  const handleCreate = useCallback(() => {
    navigate('/pbx/call-center/vip-lists/new');
  }, [navigate]);

  const handleEdit = useCallback(
    (list: VIPList) => {
      navigate(`/pbx/call-center/vip-lists/${(list as any).uuid || list.id}`);
    },
    [navigate]
  );

  const handleDuplicate = useCallback(
    (list: VIPList) => {
      navigateToVipListDuplicate(navigate, (list as any).uuid || list.id);
    },
    [navigate]
  );

  const handleDeleteClick = useCallback((list: VIPList) => {
    setDeleteModal({ isOpen: true, list });
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeleteModal({ isOpen: false, list: null });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.list) return;

    setIsDeleting(true);
    try {
      await deleteVipListById(deleteModal.list.id);

      showSuccess(t('vipLists.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, list: null });
      refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('vipLists.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.list, showError, showSuccess, t, refresh]);

  const renderRowActions = useCallback(
    (item: VIPList) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common.edit')} position="top">
          <button
            onClick={() => handleEdit(item)}
            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors"
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common.duplicate')} position="top">
          <button
            onClick={() => handleDuplicate(item)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
          >
            <Copy className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common.delete')} position="top">
          <button
            onClick={() => handleDeleteClick(item)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleEdit, handleDuplicate, handleDeleteClick, t]
  );

  return (
    <div className="p-6">
      <DataGridSimple
        data={data}
        columns={columns}
        loading={loading}
        pagination={pagination}
        searchQuery={searchQuery}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSearch={handleSearch}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onColumnToggle={handleColumnToggle}
        onAdd={handleCreate}
        title={t('vipLists.title')}
        description={t('vipLists.description')}
        titleIcon={<Crown className="h-5 w-5 text-primary" />}
        addButtonText={t('vipLists.actions.add')}
        renderActions={renderRowActions}
        emptyMessage={t('vipLists.messages.emptyMessage')}
      />

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('vipLists.messages.deleteConfirmTitle')}
        message={t('vipLists.messages.deleteConfirm', { name: deleteModal.list?.name })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}

